/* eslint-disable react-refresh/only-export-components */
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import {
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useMemberStore,
    useMemoStore,
    usePipelineStore,
    useUserStore,
    useWorkspaceStore,
} from '@/stores';

import {
    canonicalizeWorkspacePath,
    dedupeRecentRoutes,
    getSettingsRouteTitle,
    RecentWorkspaceResourceType,
    RecentWorkspaceRoute,
    titleizeRouteSegment,
} from './navigationHistory';

export type { RecentWorkspaceResourceType, RecentWorkspaceRoute } from './navigationHistory';

interface NavigationHistoryState {
    entries: RecentWorkspaceRoute[];
    index: number;
    scopeKey?: string;
}

interface NavigationHistoryContextValue {
    canGoBack: boolean;
    canGoForward: boolean;
    clearHistory: () => void;
    currentPath: string;
    goBack: () => void;
    goForward: () => void;
    recentRoutes: RecentWorkspaceRoute[];
}

const RECENT_ROUTE_LIMIT = 10;
const NavigationHistoryContext = createContext<NavigationHistoryContextValue | undefined>(undefined);

const scheduleIdleTask = (callback: () => void, timeout = 500) => {
    if (typeof window === 'undefined') {
        return () => undefined;
    }

    const idleWindow = window as typeof window & {
        requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
        cancelIdleCallback?: (handle: number) => void;
    };

    if (idleWindow.requestIdleCallback && idleWindow.cancelIdleCallback) {
        const handle = idleWindow.requestIdleCallback(callback, { timeout });

        return () => idleWindow.cancelIdleCallback?.(handle);
    }

    const handle = window.setTimeout(callback, 0);

    return () => window.clearTimeout(handle);
};

const routeTitle = (path: string) => {
    if (path === '/') {
        return 'Home';
    }

    const segments = path.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];

    return titleizeRouteSegment(lastSegment ?? path);
};

const scopedRecentRoutesKey = (userId?: string, memberId?: string, workspaceId?: string) => {
    if (!userId || !workspaceId) {
        return undefined;
    }

    return `recentWorkspaceRoutes-${userId}-${memberId ?? 'member'}-${workspaceId}`;
};

const normalizeStoredRoute = (entry: string | RecentWorkspaceRoute): RecentWorkspaceRoute => {
    const storedEntry =
        typeof entry === 'string'
            ? {
                  path: entry,
                  resourceType: 'route' as const,
                  title: routeTitle(entry),
                  subtitle: 'Route',
                  visitedAt: new Date().toISOString(),
              }
            : entry;
    const canonicalPath = canonicalizeWorkspacePath(storedEntry.path);
    const settingsRoute = canonicalPath === '/settings' || canonicalPath.startsWith('/settings/');

    return {
        ...storedEntry,
        path: canonicalPath,
        resourceType: settingsRoute ? 'settings' : storedEntry.resourceType,
        title: settingsRoute
            ? getSettingsRouteTitle(canonicalPath)
            : canonicalPath === '/' && storedEntry.title.toLowerCase() !== 'home'
              ? 'Home'
              : storedEntry.title,
        subtitle: settingsRoute ? 'Settings' : canonicalPath === '/' ? 'Home' : storedEntry.subtitle,
    };
};

const readRecentRoutes = (key?: string): RecentWorkspaceRoute[] => {
    if (!key || typeof localStorage === 'undefined') {
        return [];
    }

    // Legacy history has no account owner and must not appear in another account's menu.
    const stored = localStorage.getItem(key);

    if (!stored) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(stored);

        if (!Array.isArray(parsed)) {
            return [];
        }

        const entries = parsed
            .filter(
                (entry): entry is string | RecentWorkspaceRoute =>
                    typeof entry === 'string' ||
                    (typeof entry === 'object' && entry !== null && 'path' in entry && typeof entry.path === 'string'),
            )
            .map(normalizeStoredRoute)
            .filter((entry) => entry.resourceType !== 'lifecycles' && !entry.path.startsWith('/lifecycles'));

        return dedupeRecentRoutes(entries).slice(0, RECENT_ROUTE_LIMIT);
    } catch {
        return [];
    }
};

const recentRoutesFromHistory = (entries: RecentWorkspaceRoute[]) =>
    dedupeRecentRoutes([...entries].reverse()).slice(0, RECENT_ROUTE_LIMIT);

const resolveRouteMatch = (path: string) => {
    const segments = path.split('/').filter(Boolean);
    const baseRoute = segments[0];
    const secondSegment = segments[1];

    if (path === '/') {
        return { resourceType: 'home' as const };
    }

    if (baseRoute === 'relations') {
        if (secondSegment === 'dashboards' || secondSegment === 'lists' || secondSegment === 'tasks') {
            return { resourceType: secondSegment } as {
                resourceType: 'dashboards' | 'lists' | 'tasks';
            };
        }

        return { resourceType: 'relations' as const };
    }

    const knownRoutes: Partial<Record<string, RecentWorkspaceResourceType>> = {
        archive: 'archive',
        dashboards: 'dashboards',
        drafts: 'drafts',
        folders: 'folders',
        inbox: 'inbox',
        investments: 'investments',
        lists: 'lists',
        memos: 'memos',
        pipelines: 'pipelines',
        profile: 'profile',
        search: 'search',
        settings: 'settings',
        teams: 'teams',
        trash: 'trash',
    };
    const resourceType = knownRoutes[baseRoute];

    if (!resourceType) {
        return { resourceType: 'route' as const };
    }

    const detailResourceTypes: RecentWorkspaceResourceType[] = [
        'dashboards',
        'folders',
        'investments',
        'lists',
        'memos',
        'pipelines',
        'teams',
    ];

    return detailResourceTypes.includes(resourceType) && secondSegment
        ? { resourceId: secondSegment, resourceType }
        : { resourceType };
};

interface NavigationHistoryProviderProps {
    children: ReactNode;
}

interface ScopedNavigationHistoryProviderProps extends NavigationHistoryProviderProps {
    storageKey?: string;
}

const ScopedNavigationHistoryProvider: FC<ScopedNavigationHistoryProviderProps> = ({ children, storageKey }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const navigationType = useNavigationType();

    const folders = useFolderStore((state) => state.folders);
    const selectedFolder = useFolderStore((state) => state.selectedFolder);
    const investments = useInvestmentStore((state) => state.investments);
    const selectedInvestment = useInvestmentStore((state) => state.selectedInvestment);
    const memos = useMemoStore((state) => state.memos);
    const selectedMemo = useMemoStore((state) => state.selectedMemo);
    const pipelines = usePipelineStore((state) => state.pipelines);
    const selectedPipeline = usePipelineStore((state) => state.selectedPipeline);
    const dashboards = useDashboardStore((state) => state.dashboards);
    const selectedDashboard = useDashboardStore((state) => state.selectedDashboard);
    const lists = useListStore((state) => state.lists);
    const selectedList = useListStore((state) => state.selectedList);

    const [history, setHistory] = useState<NavigationHistoryState>(() => {
        const storedRecents = readRecentRoutes(storageKey);
        const entries = [...storedRecents].reverse();

        return {
            entries,
            index: entries.length - 1,
            scopeKey: storageKey,
        };
    });
    const historyRef = useRef(history);
    const pendingHistoryPathRef = useRef<string>();

    const syncHistory = useCallback((nextHistory: NavigationHistoryState) => {
        historyRef.current = nextHistory;
        setHistory(nextHistory);
    }, []);

    const recentRoutes = useMemo(() => recentRoutesFromHistory(history.entries), [history.entries]);

    useEffect(() => {
        if (!storageKey || history.scopeKey !== storageKey) {
            return;
        }

        return scheduleIdleTask(() => {
            localStorage.setItem(storageKey, JSON.stringify(recentRoutes));
        });
    }, [history.scopeKey, recentRoutes, storageKey]);

    const findResource = useCallback(
        (resourceType: RecentWorkspaceResourceType, resourceId?: string) => {
            const selectedResourceMap = {
                dashboards: selectedDashboard,
                folders: selectedFolder,
                investments: selectedInvestment,
                lists: selectedList,
                memos: selectedMemo,
                pipelines: selectedPipeline,
            };
            const resourcesMap = {
                dashboards,
                folders,
                investments,
                lists,
                memos,
                pipelines,
            };

            if (!(resourceType in selectedResourceMap)) {
                return undefined;
            }

            const indexedResourceType = resourceType as keyof typeof selectedResourceMap;
            const selectedResource = selectedResourceMap[indexedResourceType];

            if (selectedResource?.id === resourceId) {
                return selectedResource;
            }

            return resourcesMap[indexedResourceType]?.find((resource) => resource?.id === resourceId);
        },
        [
            dashboards,
            folders,
            investments,
            lists,
            memos,
            pipelines,
            selectedDashboard,
            selectedFolder,
            selectedInvestment,
            selectedList,
            selectedMemo,
            selectedPipeline,
        ],
    );

    const buildRecentRoute = useCallback(
        (path: string): RecentWorkspaceRoute => {
            const canonicalPath = canonicalizeWorkspacePath(path);
            const routeMatch = resolveRouteMatch(canonicalPath);
            const resource = findResource(routeMatch.resourceType, routeMatch.resourceId);
            const resourceRecord = resource as unknown as Record<string, unknown> | undefined;
            const stringField = (key: string) =>
                typeof resourceRecord?.[key] === 'string' ? (resourceRecord[key] as string) : undefined;
            const booleanField = (key: string) => resourceRecord?.[key] === true;
            const resourceTitle = stringField('editedName') || stringField('name') || stringField('title');
            const segments = canonicalPath.split('/').filter(Boolean);
            const title =
                resourceTitle ??
                (routeMatch.resourceType === 'settings'
                    ? getSettingsRouteTitle(canonicalPath)
                    : routeTitle(canonicalPath));
            const subtitle =
                routeMatch.resourceType === 'home'
                    ? 'Home'
                    : routeMatch.resourceType === 'settings'
                      ? 'Settings'
                      : segments[0] === 'relations'
                        ? 'Relations'
                        : routeMatch.resourceId
                          ? titleizeRouteSegment(routeMatch.resourceType)
                          : 'Route';

            return {
                path: canonicalPath,
                resourceType: routeMatch.resourceType,
                resourceId: routeMatch.resourceId,
                title,
                subtitle,
                website: stringField('website'),
                listType: stringField('type'),
                locked: booleanField('locked') || booleanField('isLocked'),
                archived: booleanField('archived') || booleanField('isArchived'),
                visitedAt: new Date().toISOString(),
            };
        },
        [findResource],
    );

    const currentPath = canonicalizeWorkspacePath(location.pathname);

    useEffect(() => {
        if (!storageKey || history.scopeKey !== storageKey || currentPath.startsWith('/lifecycles')) {
            return;
        }

        return scheduleIdleTask(() => {
            const currentHistory = historyRef.current;

            if (currentHistory.scopeKey !== storageKey) {
                return;
            }

            const recentRoute = buildRecentRoute(currentPath);
            const currentEntry = currentHistory.entries[currentHistory.index];

            if (pendingHistoryPathRef.current === currentPath) {
                pendingHistoryPathRef.current = undefined;
            }

            if (currentEntry?.path === currentPath) {
                const nextEntries = [...currentHistory.entries];

                nextEntries[currentHistory.index] = {
                    ...recentRoute,
                    visitedAt: currentEntry.visitedAt,
                };
                syncHistory({ ...currentHistory, entries: nextEntries });
                return;
            }

            if (navigationType === 'POP') {
                const existingIndex = currentHistory.entries.reduce(
                    (match, entry, index) => (entry.path === currentPath ? index : match),
                    -1,
                );

                if (existingIndex >= 0) {
                    const nextEntries = [...currentHistory.entries];

                    nextEntries[existingIndex] = {
                        ...recentRoute,
                        visitedAt: nextEntries[existingIndex].visitedAt,
                    };
                    syncHistory({ ...currentHistory, entries: nextEntries, index: existingIndex });
                    return;
                }
            }

            const appendedEntries = [...currentHistory.entries.slice(0, currentHistory.index + 1), recentRoute].slice(
                -RECENT_ROUTE_LIMIT,
            );

            syncHistory({
                ...currentHistory,
                entries: appendedEntries,
                index: appendedEntries.length - 1,
            });
        });
    }, [buildRecentRoute, currentPath, history.scopeKey, navigationType, storageKey, syncHistory]);

    const navigateHistory = useCallback(
        (offset: -1 | 1) => {
            const currentHistory = historyRef.current;
            const nextIndex = currentHistory.index + offset;
            const target = currentHistory.entries[nextIndex];

            if (!target) {
                return;
            }

            pendingHistoryPathRef.current = target.path;
            syncHistory({ ...currentHistory, index: nextIndex });
            navigate(target.path);
        },
        [navigate, syncHistory],
    );

    const goBack = useCallback(() => navigateHistory(-1), [navigateHistory]);
    const goForward = useCallback(() => navigateHistory(1), [navigateHistory]);
    const clearHistory = useCallback(() => {
        pendingHistoryPathRef.current = undefined;
        syncHistory({ entries: [], index: -1, scopeKey: storageKey });
    }, [storageKey, syncHistory]);

    const value = useMemo<NavigationHistoryContextValue>(
        () => ({
            canGoBack: history.index > 0,
            canGoForward: history.index >= 0 && history.index < history.entries.length - 1,
            clearHistory,
            currentPath,
            goBack,
            goForward,
            recentRoutes,
        }),
        [clearHistory, currentPath, goBack, goForward, history.entries.length, history.index, recentRoutes],
    );

    return <NavigationHistoryContext.Provider value={value}>{children}</NavigationHistoryContext.Provider>;
};

export const NavigationHistoryProvider: FC<NavigationHistoryProviderProps> = ({ children }) => {
    const user = useUserStore((state) => state.selectedUser);
    const member = useMemberStore((state) => state.selectedMember);
    const workspace = useWorkspaceStore((state) => state.selectedWorkspace);
    const storageKey = scopedRecentRoutesKey(user?.id, member?.id ?? user?.activeMember?.id, workspace?.id);

    return (
        <ScopedNavigationHistoryProvider key={storageKey ?? 'unscoped-navigation-history'} storageKey={storageKey}>
            {children}
        </ScopedNavigationHistoryProvider>
    );
};

export const useNavigationHistory = () => {
    const context = useContext(NavigationHistoryContext);

    if (!context) {
        throw new Error('useNavigationHistory must be used within NavigationHistoryProvider.');
    }

    return context;
};
